import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  X,
  Sparkles,
  Loader2,
  Check,
  ChevronRight,
  ShieldCheck,
  Settings,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../auth/AuthContext";
import { useHeaderStore } from "../../store/headerStore";
import {
  getMyActiveCoupon,
  generateUserCoupon,
  redeemUserCoupon,
  getAdminCouponConfig,
  updateAdminCouponConfig,
  resetMyCoupon,
  clearAllAdminClaims,
  type ClaimedCoupon,
  type CouponConfig,
} from "../../api/couponApi";
import CouponModal from "./CouponModal";
import "../../styles/coupon-page.css";

function formatCountdown(expiresAtStr?: string): { formatted: string; isExpired: boolean } {
  if (!expiresAtStr) return { formatted: "00m : 00s", isExpired: true };
  const expiryTime = new Date(expiresAtStr).getTime();
  const now = new Date().getTime();
  const diffSec = Math.max(0, Math.floor((expiryTime - now) / 1000));

  const m = Math.floor(diffSec / 60);
  const s = diffSec % 60;
  return {
    formatted: `${m}m : ${String(s).padStart(2, "0")}s`,
    isExpired: diffSec <= 0,
  };
}

function formatExpiredDate(dateStr?: string): string {
  if (!dateStr) return "expired";
  const d = new Date(dateStr);
  const day = d.getDate();
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  return `expired on ${day} ${month}, ${year}`;
}

export default function CouponPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setPageTitleOverride } = useHeaderStore();

  const storedRole = localStorage.getItem("role") || "";
  const currentRole = (user?.role ?? storedRole ?? "").toLowerCase();
  const isAdmin = currentRole === "admin" || currentRole === "super_admin";

  const [activeCoupon, setActiveCoupon] = useState<ClaimedCoupon | null>(null);
  const [loadingMyCoupon, setLoadingMyCoupon] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Live timer for ticket card on page
  const [countdownText, setCountdownText] = useState("00m : 00s");
  const [isCouponExpired, setIsCouponExpired] = useState(false);

  // Admin config states
  const [adminConfig, setAdminConfig] = useState<CouponConfig | null>(null);
  const [adminStats, setAdminStats] = useState({
    total_claims: 0,
    active_claims: 0,
    redeemed_claims: 0,
  });
  const [loadingAdminConfig, setLoadingAdminConfig] = useState(false);
  const [adminLoadError, setAdminLoadError] = useState<string | null>(null);
  const [savingAdminConfig, setSavingAdminConfig] = useState(false);
  const [conditionsInput, setConditionsInput] = useState("");

  useEffect(() => {
    setPageTitleOverride("COUPON");
    return () => setPageTitleOverride(null);
  }, [setPageTitleOverride]);

  // Load user's coupon (active, redeemed, or expired)
  const loadUserCoupon = useCallback(async () => {
    try {
      setLoadingMyCoupon(true);
      const res = await getMyActiveCoupon();
      if (res.coupon) {
        const timerInfo = formatCountdown(res.coupon.expires_at);
        setActiveCoupon(res.coupon);
        setCountdownText(timerInfo.formatted);
        setIsCouponExpired(res.coupon.status === "expired" || timerInfo.isExpired);
      } else {
        setActiveCoupon(null);
        setIsCouponExpired(false);
      }
    } catch (err) {
      console.error("Error loading active coupon:", err);
    } finally {
      setLoadingMyCoupon(false);
    }
  }, []);

  // Load admin config
  const loadAdminConfig = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingAdminConfig(true);
    setAdminLoadError(null);
    try {
      const res = await getAdminCouponConfig();
      setAdminConfig(res.config);
      setAdminStats(res.stats);
      setConditionsInput(res.config.conditions.join("\n"));
    } catch (err: any) {
      console.error("Error loading admin coupon config:", err);
      const msg = err?.response?.data?.detail || err?.message || "Failed to load campaign settings";
      setAdminLoadError(msg);
    } finally {
      setLoadingAdminConfig(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadUserCoupon();
    if (isAdmin) {
      loadAdminConfig();
    }
  }, [loadUserCoupon, isAdmin, loadAdminConfig]);

  // Live countdown timer for page card
  useEffect(() => {
    if (!activeCoupon?.expires_at) return;

    const timerInfo = formatCountdown(activeCoupon.expires_at);
    setCountdownText(timerInfo.formatted);
    const expiredNow = activeCoupon.status === "expired" || timerInfo.isExpired;
    setIsCouponExpired(expiredNow);

    if (expiredNow) {
      return;
    }

    const interval = setInterval(() => {
      const currentTimer = formatCountdown(activeCoupon.expires_at);
      setCountdownText(currentTimer.formatted);
      if (currentTimer.isExpired) {
        setIsCouponExpired(true);
        setActiveCoupon((prev) => (prev ? { ...prev, status: "expired" } : null));
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeCoupon?.expires_at, activeCoupon?.status]);

  // Generate / Claim coupon
  const handleGenerateCoupon = async () => {
    setGenerating(true);
    try {
      const res = await generateUserCoupon();
      setActiveCoupon(res.coupon);
      setIsCouponExpired(false);
      setModalOpen(true);
      toast.success(res.message);
      if (isAdmin) {
        loadAdminConfig();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to generate coupon. Please try again.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // Reset current user's coupon (to re-test State 1 "Generate Coupon")
  const handleResetMyCoupon = async () => {
    setResetting(true);
    try {
      await resetMyCoupon();
      setActiveCoupon(null);
      setIsCouponExpired(false);
      setModalOpen(false);
      toast.success("Coupon reset. You can now test generating a coupon!");
      if (isAdmin) {
        loadAdminConfig();
      }
    } catch (err: any) {
      toast.error("Failed to reset coupon.");
    } finally {
      setResetting(false);
    }
  };

  // Admin clear all claims in DB
  const handleClearAllClaims = async () => {
    if (!window.confirm("Are you sure you want to clear all coupon claims from the database?")) {
      return;
    }
    setResetting(true);
    try {
      const res = await clearAllAdminClaims();
      setActiveCoupon(null);
      setIsCouponExpired(false);
      setModalOpen(false);
      toast.success(res.message);
      loadAdminConfig();
    } catch (err: any) {
      toast.error("Failed to clear claims.");
    } finally {
      setResetting(false);
    }
  };

  // Redeem coupon
  const handleRedeem = async (code: string) => {
    setRedeeming(true);
    try {
      const res = await redeemUserCoupon(code);
      setActiveCoupon(res.coupon);
      toast.success(res.message);
      setModalOpen(false);
      navigate(`/subscription?coupon=${encodeURIComponent(code)}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to redeem coupon.";
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  // Admin save configuration
  const handleSaveAdminConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminConfig) return;

    setSavingAdminConfig(true);
    try {
      const parsedConditions = conditionsInput
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);

      const res = await updateAdminCouponConfig({
        ...adminConfig,
        conditions: parsedConditions,
      });

      setAdminConfig(res.config);
      toast.success(res.message);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to update coupon settings.";
      toast.error(msg);
    } finally {
      setSavingAdminConfig(false);
    }
  };

  const isRedeemed = activeCoupon?.status === "redeemed";
  const isExpired = activeCoupon?.status === "expired" || isCouponExpired;

  return (
    <div>
      <div className="coupon-page-container">
        {/* Back Link */}
        <Link to="/dashboard" className="help-back-btn">
          <ArrowLeft size={16} />
          <span>Back</span>
        </Link>

        {/* Page Title */}
        <div className="coupon-page-header">
          <h1 className="coupon-page-title">Coupon</h1>
        </div>

        {/* Conditional Rendering: Initial State vs Generated (Active/Expired) Card State */}
        {loadingMyCoupon ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#94a3b8" }}>
            <Loader2 size={18} className="animate-spin text-indigo-400" />
            <span>Loading coupon details...</span>
          </div>
        ) : !activeCoupon ? (
          /* State 1: Before Generating (Matching Image 1) */
          <div>
            <p className="coupon-page-desc">
              Generate a personalized coupon and unlock the exclusive offer tailored just for you.
            </p>

            <button
              type="button"
              className="coupon-generate-btn"
              onClick={handleGenerateCoupon}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <span>Generate Coupon</span>
              )}
            </button>
          </div>
        ) : (
          /* State 2 & 3: Generated Ticket Card on Page (Active or Expired) */
          <div className="coupon-active-ticket-wrap">
            <div className={`coupon-ticket-card-page ${isExpired ? "is-expired" : ""}`}>
              <div className="coupon-ticket-main-row">
                {/* Left Part */}
                <div className="coupon-ticket-left">
                  <h2 className={`coupon-discount-text ${isExpired ? "is-expired" : ""}`}>
                    {activeCoupon.discount_title}
                  </h2>
                  <span className={`coupon-duration-text ${isExpired ? "is-expired" : ""}`}>
                    {activeCoupon.duration_text}
                  </span>
                </div>

                {/* Perforation divider with top and bottom notches */}
                <div className="coupon-perforation-divider">
                  <div className="coupon-notch-top" />
                  <div className="coupon-notch-bottom" />
                </div>

                {/* Right Part */}
                <div className="coupon-ticket-right">
                  <div className="coupon-status-badge-row">
                    {isExpired ? (
                      <span className="coupon-expired-text-badge">EXPIRED</span>
                    ) : (
                      <span className="coupon-active-badge">
                        {isRedeemed ? "REDEEMED" : "ACTIVE"}
                      </span>
                    )}
                  </div>

                  <div className={`coupon-code-box ${isExpired ? "is-expired" : ""}`}>
                    <span className="coupon-code-text">{activeCoupon.coupon_code}</span>

                    {!isExpired && (
                      isRedeemed ? (
                        <button
                          type="button"
                          className="coupon-redeem-btn is-redeemed"
                          disabled
                        >
                          <Check size={12} />
                          <span>Redeemed</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="coupon-redeem-btn"
                          onClick={() => handleRedeem(activeCoupon.coupon_code)}
                          disabled={redeeming}
                        >
                          {redeeming ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            "Redeem"
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Info Bar */}
              <div className="coupon-ticket-bottom-bar">
                <span className="coupon-expires-text">
                  {isExpired ? (
                    formatExpiredDate(activeCoupon.expires_at)
                  ) : isRedeemed ? (
                    <span style={{ color: "#4ade80" }}>Discount activated on your plan</span>
                  ) : (
                    <>
                      expires in{" "}
                      <span className="coupon-expires-highlight">
                        {countdownText}
                      </span>
                    </>
                  )}
                </span>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                    type="button"
                    className="coupon-details-link-btn"
                    onClick={() => setModalOpen(true)}
                  >
                    details
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Promotional Call to Action: Only rendered when coupon is active & unexpired */}
            {!isExpired && (
              <div className="coupon-promo-footer">
                <p className="coupon-promo-text">
                  Enjoy this exclusive offer by choosing your perfect plan.
                </p>

                <Link
                  to={`/subscription?coupon=${encodeURIComponent(activeCoupon.coupon_code)}`}
                  className="coupon-plan-link"
                >
                  <span>Choose plan now</span>
                  <ChevronRight size={15} />
                </Link>
              </div>
            )}
          </div>
        )}

        {/* =====================================================================
            Admin Coupon Management Section (Admin Only)
            ===================================================================== */}
        {isAdmin && (
          <div className="admin-coupon-config-section">
            <div className="admin-coupon-config-header">
              <h2 className="admin-coupon-config-title">
                <Settings size={18} style={{ color: "#818cf8" }} />
                Admin Coupon Campaign Manager
              </h2>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  type="button"
                  className="admin-coupon-action-pill-btn"
                  onClick={handleResetMyCoupon}
                  disabled={resetting}
                  title="Reset your own coupon claim to test client generation flow"
                >
                  <RefreshCw size={13} className={resetting ? "animate-spin" : ""} />
                  <span>Reset My Coupon</span>
                </button>
                <button
                  type="button"
                  className="admin-coupon-action-pill-btn danger"
                  onClick={handleClearAllClaims}
                  disabled={resetting}
                  title="Clear all coupon claims in the database"
                >
                  <Trash2 size={13} />
                  <span>Clear All Claims</span>
                </button>
              </div>
            </div>

            {loadingAdminConfig && !adminConfig ? (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "28px 0", color: "#94a3b8" }}>
                <Loader2 size={18} className="animate-spin text-indigo-400" />
                <span>Loading admin campaign settings...</span>
              </div>
            ) : adminLoadError && !adminConfig ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  background: "rgba(239, 68, 68, 0.08)",
                  border: "1px solid rgba(239, 68, 68, 0.25)",
                  borderRadius: "12px",
                  margin: "12px 0 20px",
                }}
              >
                <span style={{ color: "#f87171", fontSize: "0.88rem" }}>{adminLoadError}</span>
                <button
                  type="button"
                  className="admin-coupon-action-pill-btn"
                  onClick={loadAdminConfig}
                >
                  <RefreshCw size={13} />
                  <span>Retry</span>
                </button>
              </div>
            ) : adminConfig ? (
              <>
                {/* Stats Row */}
                <div className="admin-coupon-stats-row">
                  <div className="admin-coupon-stat-card">
                    <span className="admin-coupon-stat-val">{adminStats.total_claims}</span>
                    <span className="admin-coupon-stat-label">Total Claims</span>
              </div>
              <div className="admin-coupon-stat-card">
                <span className="admin-coupon-stat-val" style={{ color: "#818cf8" }}>
                  {adminStats.active_claims}
                </span>
                <span className="admin-coupon-stat-label">Active Coupons</span>
              </div>
              <div className="admin-coupon-stat-card">
                <span className="admin-coupon-stat-val" style={{ color: "#34d399" }}>
                  {adminStats.redeemed_claims}
                </span>
                <span className="admin-coupon-stat-label">Redeemed Coupons</span>
              </div>
            </div>

            {/* Admin Form Card */}
            <form onSubmit={handleSaveAdminConfig} className="admin-coupon-form-card">
              <div className="admin-coupon-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">Coupon Code</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={adminConfig.code}
                    onChange={(e) =>
                      setAdminConfig({ ...adminConfig, code: e.target.value.toUpperCase() })
                    }
                    placeholder="e.g. 44FC5"
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Discount Title</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={adminConfig.discount_title}
                    onChange={(e) =>
                      setAdminConfig({ ...adminConfig, discount_title: e.target.value })
                    }
                    placeholder="e.g. 10% off"
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Duration Text</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={adminConfig.duration_text}
                    onChange={(e) =>
                      setAdminConfig({ ...adminConfig, duration_text: e.target.value })
                    }
                    placeholder="e.g. for 3 months"
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Validity (Minutes)</label>
                  <input
                    type="number"
                    className="admin-form-input"
                    value={adminConfig.expiry_minutes}
                    onChange={(e) =>
                      setAdminConfig({
                        ...adminConfig,
                        expiry_minutes: parseInt(e.target.value) || 30,
                      })
                    }
                    min={1}
                    max={10080}
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Applicable Plan</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={adminConfig.applicable_plan}
                    onChange={(e) =>
                      setAdminConfig({ ...adminConfig, applicable_plan: e.target.value })
                    }
                    placeholder="e.g. Business plan"
                    required
                  />
                </div>

                <div className="admin-form-group" style={{ display: "flex", justifyContent: "center" }}>
                  <label className="admin-form-label" style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", marginTop: "24px" }}>
                    <input
                      type="checkbox"
                      checked={adminConfig.is_active}
                      onChange={(e) =>
                        setAdminConfig({ ...adminConfig, is_active: e.target.checked })
                      }
                      style={{ width: "16px", height: "16px", accentColor: "#6366f1" }}
                    />
                    <span>Campaign Active</span>
                  </label>
                </div>

                <div className="admin-form-group full-width">
                  <label className="admin-form-label">
                    Conditions (one rule per line)
                  </label>
                  <textarea
                    rows={4}
                    className="admin-form-input"
                    value={conditionsInput}
                    onChange={(e) => setConditionsInput(e.target.value)}
                    placeholder="This coupon is only valid for Business plan.&#10;This coupon can only be redeemed once."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={savingAdminConfig}
                className="admin-coupon-save-btn"
              >
                {savingAdminConfig ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Campaign Settings</span>
                )}
              </button>
            </form>
          </>
        ) : null}
      </div>
    )}
      </div>

      {/* Popup Modal (Matching Image 2) */}
      <CouponModal
        coupon={activeCoupon}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onRedeem={handleRedeem}
        redeeming={redeeming}
      />
    </div>
  );
}
